/*
 * Copyright (c) 2016-2020 Martin Donath <martin.donath@squidfunk.com>
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to
 * deal in the Software without restriction, including without limitation the
 * rights to use, copy, modify, merge, publish, distribute, sublicense, and/or
 * sell copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NON-INFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING
 * FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS
 * IN THE SOFTWARE.
 */

import { BehaviorSubject, Subject } from "rxjs"

/* ----------------------------------------------------------------------------
 * Functions
 * ------------------------------------------------------------------------- */

/**
 * Retrieve location
 *
 * This function will return a `URL` object (and not `Location`) in order to
 * normalize typings across the application. Furthermore, locations need to be
 * tracked without setting them and `Location` is a singleton which represents
 * the current location.
 *
 * @return URL
 */
export function getLocation(): URL {
  return new URL(location.href)
}

/**
 * Set location
 *
 * @param url - URL to change to
 */
export function setLocation(url: URL): void {
  location.href = url.href
}

/* ------------------------------------------------------------------------- */

/**
 * Check whether a URL is a local link or a file (except `.html`)
 *
 * @param url - URL or HTML anchor element
 * @param ref - Reference URL
 *
 * @return Test result
 */
export function isLocalLocation(
  url: URL | HTMLAnchorElement,
  ref: URL | Location = location
): boolean {
  return url.host === ref.host
      && /^(?:\/[\w-]+)*(?:\/?|\.html)$/i.test(url.pathname)
}

/**
 * Check whether a URL is an anchor link on the current page
 *
 * @param url - URL or HTML anchor element
 * @param ref - Reference URL
 *
 * @return Test result
 */
export function isAnchorLocation(
  url: URL | HTMLAnchorElement,
  ref: URL | Location = location
): boolean {
  return url.pathname === ref.pathname
      && url.hash.length > 0
}

/* ------------------------------------------------------------------------- */

/**
 * Watch location
 *
 * @return Location subject
 */
export function watchLocation(): Subject<URL> {
  return new BehaviorSubject<URL>(getLocation())
}
